// Destruction (James, 2026-10-21): the town's buildings take damage, start to smoulder, catch fire
// and burn down into a pile of rubble; standing stones and stacked stone archways topple when
// something big enough (a rocket, an impact grenade) goes off beside them.
//
// The server owns every number here. It sends three small reliable events:
//   Ev.Building  (island, building, stage, hp byte)  when a building's hp moves a notch or its stage changes
//   Ev.Topple    (island, stone, blast xyz, force)   once, the moment a stone goes over
//   Ev.StoneRest (island, stone, block, pose)        once per block, where the server's fall left it
// Both ends apply them to their own World with the same functions below, so collision (a fallen
// building's rubble heap, a toppled archway gone, the fallen blocks lying where they came to rest)
// and the burning walls that set a cowboy alight are identical on the server and in every client's
// prediction. The fall itself is a rigid-body simulation (sim/stonefall.ts) the server runs tick by
// tick: falling blocks crush whoever they land on. Clients run the same fall from the same Ev.Topple
// to draw it and settle onto the server's resting poses (client/render/world/stones.ts).
//
// James's calls (2026-10-21): rubble heaps and fallen stones are cover and block paths; a building
// coming down hurts anyone beside it; a burning building sets robots alight too; the landform
// spires and stone arches can be brought down (big ones take a few rockets).
//
// Godot reference: scripts/islands/destructible_building.gd (hp from volume, smoke 0.35, fire 0.12,
// explosives hit buildings harder) and rock_formation.gd (blast-only, per-kind break force).

import { Rng, clamp, hash32, len2, secs } from './core';
import type { Box, Cyl, World } from './world';
import type { Enemy } from './enemies';
import type { PlayerState } from './player';

export const enum BStage { Intact = 0, Damaged = 1, Smoulder = 2, Burning = 3, Collapsed = 4 }

export const BUILDING = {
  /** hit points per cubic metre above the street (a typical front has ~2000) */
  hpPerM3: 3.5,
  /** stage thresholds, as a fraction of max hp left */
  damaged: 0.6, smoulder: 0.35, burning: 0.15,
  /** once it's alight a building burns itself down over this long */
  burnSecs: 20,
  /** explosives hit a building this much harder than their robot damage (Godot ANTI_BUILDING_EXPLOSIVE_MULT) */
  blastMul: 4,
  /** the rubble pile's walkable top above the street (a step, so it never blocks the street) */
  rubbleH: 0.45,
  /** how close a cowboy's capsule has to come to a burning building to catch (m beyond touching) */
  touch: 0.3,
  /** the heap left behind: its core (fraction of the footprint, height) and the spill into the street (depth, height) */
  heapCore: 0.7, heapH: 1.5, spillD: 2, spillH: 1.0,
  /** a building coming down hurts everyone within this of its walls */
  collapseReach: 1.5, collapseDmg: 70, collapseKnock: 9,
  /** a robot this close to a burning building catches (m beyond touching), checked every few ticks */
  robotTouch: 1.2,
} as const;

/**
 * Flying into a house (James, 2026-10-24: "punch a hole in the house or just knock it down... think about what's
 * coolest"): at full flight speed a cowboy bursts through the front wall, through the rooms and out the back,
 * leaving a splintered hole each side. Each wall he breaks costs the building a share of its hp, so the first
 * pass leaves it holed and the second brings it down behind him; one that's already smouldering comes down
 * as he bursts out. The flier doesn't take the collapse (he's the one doing it).
 */
export const BREACH = {
  /** horizontal flight speed (m/s) that goes through a wall instead of bouncing off it (level cruise is 12.6) */
  speed: 11,
  /** speed kept through each wall */
  keep: 0.82,
  /** share of the building's max hp each wall broken takes */
  wallFrac: 0.22,
  /** holes kept per island (oldest go first); the renderer's uniform array is this long */
  maxHoles: 16,
  /** hole radius (m) */
  r: 1.05,
} as const;

/**
 * Flying into stone (James, 2026-10-24: "fly full speed into a stacked pile of rocks and they topple with
 * realistic physics"): a bonk at this speed or more knocks over the standing stone or stone archway it hit,
 * away from the flier, through the same server-run fall as a rocket. Each m/s is worth `force` of blast force,
 * so a full dive into a landform chips its hp like a grenade would.
 */
export const BUMP_STONE = { speed: 10, force: 6.5, reach: 0.9 } as const;

/** face: 0 +x, 1 -x, 2 +z, 3 -z; entry: the wall he broke in through (its boards bent into the room) */
export interface Hole { x: number; y: number; z: number; face: number; b: number; entry: boolean }

/** Falling stone: how fast a block must be moving to hurt, and what it does. */
export const CRUSH = { speed: 4, player: 55, robot: 140, knock: 7 } as const;

/** Burning ticks a building gives a cowboy who touches it (put out sooner by a dive roll or water). */
export const BUILDING_FIRE_TICKS = secs(5);

export const enum StoneKind { Monolith = 0, Archway = 1, Landform = 2 }

export interface Pose { x: number; y: number; z: number; qx: number; qy: number; qz: number; qw: number }

/** One rigid block of a stone: centre, half extents, yaw. The physics fall starts from exactly this. */
export interface StoneBlock { x: number; y: number; z: number; hx: number; hy: number; hz: number; yaw: number }

export interface Stone {
  kind: StoneKind;
  /** index of the source obstacle: the pillar cyl (monolith) or this archway's variant */
  src: number;
  /** blast force needed to topple it (explosion damage at the stone, after falloff) */
  force: number;
  blocks: StoneBlock[];
  /** bounds, for blast distance */
  x0: number; x1: number; y0: number; y1: number; z0: number; z1: number;
  boxes: Box[]; cyls: Cyl[];
  /** a monolith's standing-stone cylinder as generated (collision is parked once it falls) */
  pillar: Cyl | null;
  /** landforms: blast force still needed to bring it down (big ones take a few rockets); others topple in one */
  hp: number;
  /** landforms: the stub left standing (a static block the fall lands on) and the cylinders cut down to it */
  stumps: StoneBlock[];
  cut: { c: Cyl; y1: number }[];
  /** landforms: the landform's variant (client/render/world/landforms.ts draws it) */
  land: number;
  toppled: boolean;
  /** the blast that felled it, for the client's fall (settled = a late joiner: show it already down) */
  fall: { x: number; y: number; z: number; force: number; settled: boolean; by: number } | null;
  /** where each block came to rest (server's fall), and the collision laid there */
  rest: (Pose | null)[];
  restCyls: Cyl[];
}

export interface Building {
  box: Box;
  max: number;
  hp: number;
  stage: BStage;
  /** hp byte last sent (server) / received (client) */
  hpByte: number;
  /** the intact roof height, before collapse lowers the box */
  top: number;
  /** server: tick of the last hp event, to throttle them */
  sentAt: number;
  /** server: the last cowboy to hurt it (collapse and fire kills are theirs), or -1 */
  by: number;
  /** the heap's collision, laid when it comes down */
  rubble: Box[];
  /** server: a cowboy breaking through it right now is spared its collapse (-1 none) */
  spare: number;
}

/**
 * Topple force per stone kind (Godot _BREAK_FORCE scaled to web damage: rocket 120, impact grenade 85).
 * A landform takes any blast at least this big off its hp instead (LANDFORM_HP).
 */
export const STONE_FORCE = { [StoneKind.Monolith]: 58, [StoneKind.Archway]: 66, [StoneKind.Landform]: 60 } as const;
/** A landform's hp: this much blast force per 150 m^3 of rock above its stump, 1 to 5 times over. */
export const LANDFORM_HP = 80;

/** A standing stone the renderer draws as a mossy boulder pile instead of a monolith (props.ts). */
export const isBoulderPile = (c: Cyl) => hash32(c.variant, 0xc105) % 3 === 0;

export function stageOf(frac: number): BStage {
  if (frac <= 0) return BStage.Collapsed;
  if (frac <= BUILDING.burning) return BStage.Burning;
  if (frac <= BUILDING.smoulder) return BStage.Smoulder;
  if (frac <= BUILDING.damaged) return BStage.Damaged;
  return BStage.Intact;
}

export class Destruct {
  readonly buildings: Building[] = [];
  readonly stones: Stone[] = [];
  /** any building burning right now (fast reject for the per-tick touch test) */
  burning = 0;
  /** server: stones toppled this tick whose falls sim/stonefall.ts has yet to start */
  toFall: number[] = [];
  /** holes cowboys punched through house walls (BREACH), oldest first; `holeVer` ticks on every change */
  readonly holes: Hole[] = [];
  holeVer = 0;

  constructor(private world: World) {
    for (const b of world.boxes) {
      if (b.kind !== 'building') continue;
      const ty = world.town.y;
      const max = Math.round(2 * b.hx * 2 * b.hz * (b.y1 - ty) * BUILDING.hpPerM3);
      this.buildings.push({ box: b, max, hp: max, stage: BStage.Intact, hpByte: 255, top: b.y1, sentAt: -999, by: -1, rubble: [], spare: -1 });
    }
    // standing stones: every monolith pillar (the boulder piles are piles already)
    for (const c of world.cyls) {
      if (c.kind !== 'pillar' || isBoulderPile(c)) continue;
      const g = world.terrain(c.x, c.z);
      const base = Math.max(c.y0, g - 0.2);
      const r = c.r * 0.82;
      this.addStone(StoneKind.Monolith, c.variant, [{ x: c.x, y: (base + c.y1) / 2, z: c.z, hx: r, hy: (c.y1 - base) / 2, hz: r, yaw: 0 }], [], [c]);
    }
    for (const a of world.archways) this.addStone(StoneKind.Archway, a.variant, a.blocks, a.boxes, []);
    // landforms: spires (hoodoos too) and stone arches / canyon bridges, broken into the chunks they fall as
    const seen = new Set<number>();
    for (const c of world.cyls) if (c.kind === 'spire') this.addSpire(c);
    for (const b of world.boxes) {
      if (b.kind !== 'arch' || seen.has(b.variant)) continue;
      seen.add(b.variant);
      this.addArch(b, world.cyls.filter((c) => c.kind === 'archleg' && c.variant === b.variant));
    }
  }

  private addStone(kind: StoneKind, src: number, blocks: StoneBlock[], boxes: Box[], cyls: Cyl[], stumps: StoneBlock[] = [], cut: { c: Cyl; y1: number }[] = [], land = -1) {
    let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9, z0 = 1e9, z1 = -1e9;
    for (const b of [...blocks, ...stumps]) {
      const ex = Math.max(b.hx, b.hz);
      x0 = Math.min(x0, b.x - ex); x1 = Math.max(x1, b.x + ex);
      z0 = Math.min(z0, b.z - ex); z1 = Math.max(z1, b.z + ex);
      y0 = Math.min(y0, b.y - b.hy); y1 = Math.max(y1, b.y + b.hy);
    }
    let vol = 0;
    for (const b of blocks) vol += 8 * b.hx * b.hy * b.hz;
    const hp = kind === StoneKind.Landform ? LANDFORM_HP * clamp(vol / 150, 1, 5) : 0;
    this.stones.push({
      kind, src, force: STONE_FORCE[kind], blocks, x0, x1, y0, y1, z0, z1, boxes, cyls, pillar: kind === StoneKind.Monolith && cyls[0] ? { ...cyls[0] } : null,
      hp, stumps, cut, land, toppled: false, fall: null, rest: blocks.map(() => null), restCyls: [],
    });
  }

  /** The highest ground under a landform's footprint: it breaks above this, so nothing starts buried. */
  private highGround(c: Cyl) {
    let g = this.world.terrain(c.x, c.z);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      for (const f of [0.5, 1]) g = Math.max(g, this.world.terrain(c.x + Math.cos(a) * c.r * f, c.z + Math.sin(a) * c.r * f));
    }
    return Math.max(g, c.y0 + 1);
  }

  /** A spire breaks off a stub above the ground and comes down as a few drums of rock. */
  private addSpire(c: Cyl) {
    const rng = new Rng(hash32(c.variant, Math.round(c.x * 10), Math.round(c.z * 10), 0x5b1e));
    const stubTop = this.highGround(c) + clamp(c.r * 0.6, 1.2, 2.6);
    if (c.y1 - stubTop < 2) return;
    const h = c.y1 - stubTop;
    // slabs, not cubes: each piece ~2.8 radii long, so it lies on its side when it lands
    const n = clamp(Math.round(h / (c.r * 2.8)), 1, 5);
    const r = c.r * 0.78;
    const blocks: StoneBlock[] = [];
    // it snaps along uneven joints: drums of mixed height
    const cutsAt = [0];
    for (let k = 1; k < n; k++) cutsAt.push((k + rng.range(-0.3, 0.3)) / n);
    cutsAt.push(1);
    for (let k = 0; k < n; k++) {
      const y0 = stubTop + h * cutsAt[k], y1 = stubTop + h * cutsAt[k + 1];
      // the column tapers toward its crown and bulges, so its pieces aren't one gauge
      const rk = r * rng.range(0.86, 1.04) * (1 - 0.22 * (k + 0.5) / n);
      blocks.push({ x: c.x, y: (y0 + y1) / 2, z: c.z, hx: rk, hy: (y1 - y0) / 2 - 0.02, hz: rk, yaw: rng.range(0, Math.PI) });
    }
    const stub: StoneBlock = { x: c.x, y: (c.y0 + stubTop) / 2, z: c.z, hx: r, hy: (stubTop - c.y0) / 2, hz: r, yaw: 0 };
    this.addStone(StoneKind.Landform, c.variant, blocks, [], [], [stub], [{ c, y1: stubTop }], c.variant);
  }

  /** An arch's deck breaks into slabs along its span; its legs (if any) snap off above stubs and follow. */
  private addArch(b: Box, legs: Cyl[]) {
    const rng = new Rng(hash32(b.variant, Math.round(b.x * 10), Math.round(b.z * 10), 0xa2c8));
    const alongX = b.hx > b.hz;
    const L = alongX ? b.hx : b.hz, W = alongX ? b.hz : b.hx;
    const n = clamp(Math.round((2 * L) / 6), 2, 5);
    const hy = (b.y1 - b.y0) / 2;
    const blocks: StoneBlock[] = [];
    for (let k = 0; k < n; k++) {
      const u = -L + ((k + 0.5) * 2 * L) / n, hl = L / n - 0.03;
      blocks.push({ x: b.x + (alongX ? u : 0), y: (b.y0 + b.y1) / 2, z: b.z + (alongX ? 0 : u), hx: alongX ? hl : W, hy: hy - 0.02, hz: alongX ? W : hl, yaw: 0 });
    }
    const stumps: StoneBlock[] = [], cut: { c: Cyl; y1: number }[] = [];
    for (const c of legs) {
      const stubTop = this.highGround(c) + rng.range(1.4, 2.4);
      const top = Math.min(c.y1, b.y0) - 0.03;
      const r = c.r * 0.78;
      stumps.push({ x: c.x, y: (c.y0 + stubTop) / 2, z: c.z, hx: r, hy: (stubTop - c.y0) / 2, hz: r, yaw: 0 });
      cut.push({ c, y1: stubTop });
      const h = top - stubTop;
      if (h < 1) continue;
      const m = clamp(Math.round(h / (c.r * 1.8)), 1, 4);
      for (let k = 0; k < m; k++) {
        const y0 = stubTop + (h * k) / m, y1 = stubTop + (h * (k + 1)) / m;
        blocks.push({ x: c.x, y: (y0 + y1) / 2, z: c.z, hx: r, hy: (y1 - y0) / 2 - 0.02, hz: r, yaw: rng.range(0, Math.PI) });
      }
    }
    this.addStone(StoneKind.Landform, b.variant, blocks, [b], [], stumps, cut, b.variant);
  }

  /** anything shot up, burnt or knocked over (a client re-entering the same island index on a new run rebuilds it) */
  get dirty() { return this.buildings.some((b) => b.hpByte < 255) || this.stones.some((s) => s.toppled); }

  // ------------------------------------------------------------------ shared (server + client)
  /** Set a building's state (the server's own step, or a client applying Ev.Building). */
  applyBuilding(i: number, stage: BStage, hpByte: number) {
    const b = this.buildings[i];
    if (!b) return;
    b.hpByte = hpByte;
    if (b.stage === stage) return;
    b.stage = stage;
    // a fallen building is a low pile you can walk over, with a heap in the middle and a spill of
    // rubble across its front into the street: cover, and something to go round or over
    b.box.y1 = stage === BStage.Collapsed ? this.world.town.y + BUILDING.rubbleH : b.top;
    if (stage === BStage.Collapsed && !b.rubble.length) {
      const k = b.box, ty = this.world.town.y, side = rubbleSide(this.world, k);
      b.rubble.push(
        { kind: 'rubble', x: k.x, z: k.z, hx: k.hx * BUILDING.heapCore, hz: k.hz * BUILDING.heapCore, y0: ty - 0.2, y1: ty + BUILDING.heapH, variant: i },
        { kind: 'rubble', x: k.x, z: k.z + side * (k.hz + BUILDING.spillD / 2), hx: k.hx * 0.85, hz: BUILDING.spillD / 2, y0: ty - 0.2, y1: ty + BUILDING.spillH, variant: i },
      );
      for (const r of b.rubble) this.world.boxes.push(r);
    }
    let n = 0;
    for (const o of this.buildings) if (o.stage === BStage.Burning) n++;
    this.burning = n;
  }

  /** A hole in a wall (Ev.Breach, or the flier's own prediction): `code` = face | 4 on the way out. False if one is already there. */
  applyBreach(b: number, x: number, y: number, z: number, code: number) {
    const face = code & 3;
    for (const h of this.holes) if (h.b === b && h.face === face && Math.abs(h.x - x) + Math.abs(h.y - y) + Math.abs(h.z - z) < 1.2) return false;
    this.holes.push({ x, y, z, face, b, entry: (code & 4) === 0 });
    if (this.holes.length > BREACH.maxHoles) this.holes.shift();
    this.holeVer++;
    return true;
  }

  /** Where on building b's wall a flier at (x,y,z) going (dx,dz) broke through: the face he crossed, entering or leaving. */
  breachPoint(b: number, x: number, y: number, z: number, dx: number, dz: number, exit: boolean) {
    const k = this.buildings[b].box;
    // the face whose plane he crossed: the one he's moving into (exit) or out of (entry), most squarely
    const sx = exit ? Math.sign(dx) : -Math.sign(dx), sz = exit ? Math.sign(dz) : -Math.sign(dz);
    const alongX = Math.abs(dx) / k.hx >= Math.abs(dz) / k.hz;
    const face = alongX ? (sx > 0 ? 0 : 1) : (sz > 0 ? 2 : 3);
    const hy = clamp(y, k.y0 + BREACH.r * 0.9, Math.max(k.y0 + BREACH.r * 0.9, this.buildings[b].top - BREACH.r * 0.8));
    if (alongX) return { x: k.x + sx * k.hx, y: hy, z: clamp(z, k.z - Math.max(0, k.hz - 1.3), k.z + Math.max(0, k.hz - 1.3)), face };
    return { x: clamp(x, k.x - Math.max(0, k.hx - 1.3), k.x + Math.max(0, k.hx - 1.3)), y: hy, z: k.z + sz * k.hz, face };
  }

  /** A stone goes over: its collision is gone at once (the fall is drawn by the client). */
  applyTopple(i: number, bx: number, by: number, bz: number, force: number, settled = false, who = -1) {
    const s = this.stones[i];
    if (!s || s.toppled) return;
    s.toppled = true;
    s.fall = { x: bx, y: by, z: bz, force, settled, by: who };
    // parked far below the world: no support, no wall, no ceiling, no ray ever reaches it
    for (const b of s.boxes) { b.y0 = -1e9; b.y1 = -1e9; }
    for (const c of s.cyls) { c.y0 = -1e9; c.y1 = -1e9; }
    // a landform keeps its broken stub
    for (const k of s.cut) k.c.y1 = k.y1;
  }

  /**
   * One block of a fallen stone came to rest here (the server's fall, or a client applying
   * Ev.StoneRest): it lies there as cover from now on. A block that went off the island's edge
   * leaves nothing.
   */
  applyRest(i: number, k: number, pose: Pose) {
    const s = this.stones[i], b = s?.blocks[k];
    if (!b || s.rest[k]) return;
    s.rest[k] = pose;
    if (s.fall && s.rest.every((r) => r)) s.fall.settled = true;
    if (pose.y < s.y0 - 20) return;
    for (const c of restCyls(b, pose)) { s.restCyls.push(c); this.world.cyls.push(c); }
  }

  /**
   * Is a cowboy's capsule (x,z radius r, feet y, height h) touching a burning building? Walls
   * within reach, or standing on the roof. Runs in stepPlayer, so the client predicts it.
   */
  fireTouch(x: number, y: number, z: number, r: number, h: number) {
    if (!this.burning) return false;
    const reach = r + BUILDING.touch;
    for (const b of this.buildings) {
      if (b.stage !== BStage.Burning) continue;
      const k = b.box;
      if (y > k.y1 + 0.3 || y + h < k.y0) continue;
      const dx = Math.max(0, Math.abs(x - k.x) - k.hx), dz = Math.max(0, Math.abs(z - k.z) - k.hz);
      if (dx * dx + dz * dz <= reach * reach) return true;
    }
    return false;
  }

  /** Which building (index) contains this point, or -1. Shots and bolts land on the box surface. */
  buildingAt(x: number, y: number, z: number) {
    for (let i = 0; i < this.buildings.length; i++) {
      const k = this.buildings[i].box;
      if (this.buildings[i].stage === BStage.Collapsed) continue;
      if (Math.abs(x - k.x) <= k.hx + 0.08 && Math.abs(z - k.z) <= k.hz + 0.08 && y >= k.y0 - 0.08 && y <= k.y1 + 0.08) return i;
    }
    return -1;
  }
}


/** Which way a building's front faces: toward the street (the spill of its rubble goes that way). */
function rubbleSide(w: World, k: Box) { return k.z < w.town.z ? 1 : -1; }

/**
 * A resting block's collision: a short chain of upright cylinders along its longest lie, as tall
 * as the block stands there (a slab lying flat is low cover, a drum on end is a wall). Up to 3.
 */
export function restCyls(b: StoneBlock, p: Pose): Cyl[] {
  // the block's local axes in the world, scaled by its half extents
  const { qx, qy, qz, qw } = p;
  const ax = [1 - 2 * (qy * qy + qz * qz), 2 * (qx * qy + qw * qz), 2 * (qx * qz - qw * qy)].map((v) => v * b.hx);
  const ay = [2 * (qx * qy - qw * qz), 1 - 2 * (qx * qx + qz * qz), 2 * (qy * qz + qw * qx)].map((v) => v * b.hy);
  const az = [2 * (qx * qz + qw * qy), 2 * (qy * qz - qw * qx), 1 - 2 * (qx * qx + qy * qy)].map((v) => v * b.hz);
  const axes = [ax, ay, az];
  const vert = Math.abs(ax[1]) + Math.abs(ay[1]) + Math.abs(az[1]);
  // the long horizontal direction: the axis whose footprint is longest
  let best = ax, bl = 0;
  for (const a of axes) { const l = Math.hypot(a[0], a[2]); if (l > bl) { bl = l; best = a; } }
  const lx = best[0] / (bl || 1), lz = best[2] / (bl || 1);
  let half = 0, wide = 0;
  for (const a of axes) { half += Math.abs(a[0] * lx + a[2] * lz); wide += Math.abs(a[0] * -lz + a[2] * lx); }
  const r = Math.max(0.3, wide * 0.85);
  const n = clamp(Math.ceil(half / r - 0.3), 1, 3);
  const span = Math.max(0, half - r);
  const out: Cyl[] = [];
  for (let j = 0; j < n; j++) {
    const u = n === 1 ? 0 : -span + (2 * span * j) / (n - 1);
    out.push({ kind: 'stone', x: p.x + lx * u, z: p.z + lz * u, r, y0: p.y - vert, y1: p.y + vert * 0.85, variant: -2 });
  }
  return out;
}

// ------------------------------------------------------------------ server
export interface DestructHost {
  world: World;
  tick: number;
  emit(t: number, f: number[]): void;
  players: Map<number, PlayerState>;
  enemies: Enemy[];
  hurtPlayer(p: PlayerState, dmg: number, fx: number, fz: number, flags: number, kx?: number, kz?: number): boolean;
  friendlyHurt(p: PlayerState, by: number, dmg: number, fx: number, fz: number, kx?: number, kz?: number): boolean;
  damageEnemy(e: Enemy, amount: number, ap: number, by: number, flags: number, hx: number, hy: number, hz: number, bypassArmor?: boolean): boolean;
}

/** Ev ids (sim/codec.ts), repeated here so this module has no codec import cycle. */
export const EV_BUILDING = 63, EV_TOPPLE = 64, EV_STONEREST = 65, EV_BREACH = 76;

const hpByteOf = (b: Building) => (b.hp <= 0 ? 0 : Math.max(1, Math.round((b.hp / b.max) * 255)));

/** Damage a building (server); `by` is the cowboy responsible, or -1. */
export function hurtBuilding(h: DestructHost, i: number, dmg: number, by = -1) {
  const d = h.world.dest, b = d.buildings[i];
  if (!b || b.stage === BStage.Collapsed || !(dmg > 0)) return;
  b.hp = Math.max(0, b.hp - dmg);
  if (by >= 0) b.by = by;
  syncBuilding(h, i);
}

function syncBuilding(h: DestructHost, i: number) {
  const d = h.world.dest, b = d.buildings[i];
  const stage = stageOf(b.hp / b.max);
  const byte = hpByteOf(b);
  const changed = stage !== b.stage;
  // a notch of damage is worth an event (scorch creeps on every client), at most ~6 a second
  if (changed || (byte !== b.hpByte && h.tick - b.sentAt >= 10)) {
    d.applyBuilding(i, stage, byte);
    b.sentAt = h.tick;
    h.emit(EV_BUILDING, [h.world.index, i, stage, byte]);
    if (changed && stage === BStage.Collapsed) collapseHits(h, b);
  }
}

/** A building comes down: everyone beside it (or on it) is hit and thrown clear. */
function collapseHits(h: DestructHost, b: Building) {
  const k = b.box, ty = h.world.town.y, reach = BUILDING.collapseReach;
  const near = (x: number, y: number, z: number, r: number) => {
    if (y > b.top + 1 || y < ty - 2) return null;
    const dx = Math.max(0, Math.abs(x - k.x) - k.hx), dz = Math.max(0, Math.abs(z - k.z) - k.hz);
    if (dx * dx + dz * dz > (reach + r) ** 2) return null;
    const ox = x - k.x, oz = z - k.z, l = len2(ox, oz) || 1;
    return [ox / l, oz / l];
  };
  for (const p of h.players.values()) {
    if (p.id === b.spare) continue;
    const v = near(p.x, p.y, p.z, 0.4);
    if (!v) continue;
    const kn = BUILDING.collapseKnock;
    if (b.by >= 0 && b.by !== p.id) h.friendlyHurt(p, b.by, BUILDING.collapseDmg, k.x, k.z, v[0] * kn, v[1] * kn);
    else h.hurtPlayer(p, BUILDING.collapseDmg, k.x, k.z, 0, v[0] * kn, v[1] * kn);
  }
  for (const e of [...h.enemies]) {
    if (e.dead || !near(e.x, e.y, e.z, 0.6)) continue;
    h.damageEnemy(e, BUILDING.collapseDmg * 2, 4, b.by, 0, e.x, e.y + 1, e.z, true);
  }
}

/**
 * Per tick (server): a building that's alight burns itself down and sets robots touching it
 * alight; held-back hp notches go out. (Falling stones: sim/stonefall.ts stepStoneFalls.)
 */
export function stepDestruct(h: DestructHost) {
  const d = h.world.dest;
  for (let i = 0; i < d.buildings.length; i++) {
    const b = d.buildings[i];
    if (b.stage === BStage.Burning) b.hp = Math.max(0, b.hp - (b.max * BUILDING.burning) / (BUILDING.burnSecs * 60));
    else if (b.stage === BStage.Collapsed || hpByteOf(b) === b.hpByte) continue;
    syncBuilding(h, i);
  }
  if (d.burning && h.tick % 15 === 0) burnRobots(h);
}

/** A pose as Ev.StoneRest carries it: f32 position, the rotation in signed bytes, renormalised. The
 * server applies this to the raw pose and each client to what arrived: the same numbers both ends. */
export function quantizePose(p: Pose): Pose {
  const u = (v: number) => Math.max(-127, Math.min(127, Math.round(v * 127))) / 127;
  let qx = u(p.qx), qy = u(p.qy), qz = u(p.qz), qw = u(p.qw);
  const l = Math.hypot(qx, qy, qz, qw) || 1;
  qx /= l; qy /= l; qz /= l; qw /= l;
  return { x: Math.fround(p.x), y: Math.fround(p.y), z: Math.fround(p.z), qx, qy, qz, qw };
}

export const restFields = (island: number, i: number, k: number, p: Pose) => [island, i, k, p.x, p.y, p.z, p.qx, p.qy, p.qz, p.qw];

function burnRobots(h: DestructHost) {
  const d = h.world.dest, reach = BUILDING.robotTouch;
  for (const e of h.enemies) {
    if (e.dead) continue;
    for (const b of d.buildings) {
      if (b.stage !== BStage.Burning) continue;
      const k = b.box;
      if (e.y > k.y1 + 0.5 || e.y + 2 < k.y0) continue;
      const dx = Math.max(0, Math.abs(e.x - k.x) - k.hx), dz = Math.max(0, Math.abs(e.z - k.z) - k.hz);
      if (dx * dx + dz * dz > reach * reach) continue;
      e.burnT = Math.max(e.burnT, secs(3));
      e.burnOwner = b.by;
      break;
    }
  }
}

/** A stone goes over (server): collision gone, everyone told, its fall begins. */
function topple(h: DestructHost, i: number, x: number, y: number, z: number, force: number, by: number) {
  const d = h.world.dest;
  d.applyTopple(i, x, y, z, force, false, by);
  h.emit(EV_TOPPLE, [h.world.index, i, x, y, z, force, 0]);
  d.toFall.push(i); // sim/stonefall.ts runs it
}

/** A shot or a robot's bolt struck the world at (x,y,z): if that's a building, it takes the hit. */
export function shotHitsWorld(h: DestructHost, x: number, y: number, z: number, dmg: number, by = -1) {
  const i = h.world.dest.buildingAt(x, y, z);
  if (i >= 0) hurtBuilding(h, i, dmg, by);
}

/**
 * An explosion (any source; friendly fire is on and robots' bombs count too): buildings in reach
 * take blastMul x the damage with the usual half falloff; a stone close enough to a blast big
 * enough goes over (a landform takes a few such blasts). `by` = the cowboy who set it off, or -1.
 */
export function blastWorld(h: DestructHost, x: number, y: number, z: number, r: number, dmg: number, by = -1) {
  if (!(dmg > 0) || !(r > 0)) return;
  const d = h.world.dest;
  for (let i = 0; i < d.buildings.length; i++) {
    const b = d.buildings[i];
    if (b.stage === BStage.Collapsed) continue;
    const k = b.box;
    const dist = boxDist(x, y, z, k.x - k.hx, k.y0, k.z - k.hz, k.x + k.hx, k.y1, k.z + k.hz);
    if (dist > r) continue;
    hurtBuilding(h, i, dmg * (1 - (dist / r) * 0.5) * BUILDING.blastMul, by);
  }
  for (let i = 0; i < d.stones.length; i++) {
    const s = d.stones[i];
    if (s.toppled) continue;
    const dist = boxDist(x, y, z, s.x0, s.y0, s.z0, s.x1, s.y1, s.z1);
    if (dist > r) continue;
    const force = dmg * (1 - (dist / r) * 0.5);
    if (force < s.force) continue;
    if (s.kind === StoneKind.Landform) { s.hp -= force; if (s.hp > 0) continue; }
    topple(h, i, x, y, z, force, by);
  }
}

/**
 * A cowboy broke through a wall of building i at flight speed (server; stepPlayer found it, so the flier's own
 * screen already shows it). The hole is everyone's, the building takes a wall's worth of hp, and one that's
 * already smouldering comes down as he leaves it.
 */
export function breachBuilding(h: DestructHost, i: number, p: PlayerState, exit: boolean) {
  const d = h.world.dest, b = d.buildings[i];
  if (!b || b.stage === BStage.Collapsed) return;
  const sp = len2(p.vx, p.vz) || 1;
  const at = d.breachPoint(i, p.x, p.y + 0.9, p.z, p.vx / sp, p.vz / sp, exit);
  const code = at.face | (exit ? 4 : 0);
  if (d.applyBreach(i, at.x, at.y, at.z, code)) h.emit(EV_BREACH, [h.world.index, i, at.x, at.y, at.z, code, p.id]);
  b.spare = p.id;
  if (exit && b.stage >= BStage.Smoulder) b.hp = 0;
  hurtBuilding(h, i, b.max * BREACH.wallFrac, p.id);
  b.spare = -1;
}

/**
 * A cowboy flew into something hard at `speed` going (dx,dz) (server, on a bonk): whatever standing stone or
 * stone archway he hit goes over away from him; a landform takes it off its hp like a blast.
 */
export function bumpStones(h: DestructHost, p: PlayerState, speed: number, dx: number, dz: number) {
  if (speed < BUMP_STONE.speed) return;
  const d = h.world.dest, r = BUMP_STONE.reach;
  const force = speed * BUMP_STONE.force;
  for (let i = 0; i < d.stones.length; i++) {
    const s = d.stones[i];
    if (s.toppled || p.y > s.y1 || p.y + 1.8 < s.y0) continue;
    if (p.x < s.x0 - r || p.x > s.x1 + r || p.z < s.z0 - r || p.z > s.z1 + r) continue;
    // the shove comes from just behind him, at chest height: the stone goes over the way he was flying
    const bx = p.x - dx * 1.2, by = p.y + 1.1, bz = p.z - dz * 1.2;
    if (s.kind === StoneKind.Landform) { if (force < s.force) continue; s.hp -= force; if (s.hp > 0) continue; }
    topple(h, i, bx, by, bz, Math.max(force, s.force * 1.2), p.id);
  }
}

/** A cowboy rode in mid-island: tell them what's already burnt and what's already fallen. */
export function announceDestruct(h: DestructHost) {
  const d = h.world.dest;
  for (let i = 0; i < d.buildings.length; i++) {
    const b = d.buildings[i];
    if (b.stage !== BStage.Intact || b.hp < b.max) h.emit(EV_BUILDING, [h.world.index, i, b.stage, hpByteOf(b)]);
  }
  for (let i = 0; i < d.stones.length; i++) {
    const s = d.stones[i];
    if (!s.toppled || !s.fall) continue;
    // still falling: the same blast replays the same fall; settled: the blocks go straight to rest
    h.emit(EV_TOPPLE, [h.world.index, i, s.fall.x, s.fall.y, s.fall.z, s.fall.force, s.fall.settled ? 1 : 0]);
    s.rest.forEach((p, k) => { if (p) h.emit(EV_STONEREST, restFields(h.world.index, i, k, p)); });
  }
  for (const o of d.holes) h.emit(EV_BREACH, [h.world.index, o.b, o.x, o.y, o.z, o.face | (o.entry ? 0 : 4), 255]);
}

function boxDist(x: number, y: number, z: number, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  const dx = Math.max(x0 - x, 0, x - x1), dy = Math.max(y0 - y, 0, y - y1), dz = Math.max(z0 - z, 0, z - z1);
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

// ------------------------------------------------------------------ world generation
export interface Archway { variant: number; blocks: StoneBlock[]; boxes: Box[] }

/**
 * Stacked stone archways (Godot RockFormation.ARCHWAY): two piers of two or three rough-cut blocks
 * and a lintel with a capstone or two, in the open meadow. Placed with their own RNG after
 * everything else, so the rest of the island lays out exactly as before.
 */
export function layoutArchways(w: World, out: Archway[]) {
  const rng = new Rng(w.seed ^ 0xa2c4);
  const main = w.islands[0];
  const want = 3;
  let v = 0;
  for (let tries = 0; tries < 60 && out.length < want; tries++) {
    const a = rng.range(0, Math.PI * 2), rr = rng.range(16, main.radius * 0.72);
    const cx = main.cx + Math.cos(a) * rr, cz = main.cz + Math.sin(a) * rr;
    const alongX = rng.chance(0.5);
    const gap = rng.range(3.0, 4.2);
    const pier = rng.range(1.05, 1.35); // pier half-width across the span
    const depth = rng.range(0.8, 1.05); // half-depth through the arch
    const span = gap / 2 + pier; // pier centre from the arch centre
    const half = span + pier + 0.6;
    if (w.townMask(cx, cz) > 0.01 || len2(cx - w.playerSpawn.x, cz - w.playerSpawn.z) < 14) continue;
    if (len2(cx - w.gate.x, cz - w.gate.z) < 16) continue;
    if (!w.openGround(cx, cz) || !w.dryAt(cx, cz, half)) continue;
    if (!w.clearOf(cx, cz, half, 2.5)) continue;
    if (w.path.some((p) => len2(p.x - cx, p.z - cz) < half + 3)) continue;
    if (w.enemySpawns.some((p) => len2(p.x - cx, p.z - cz) < half + 3)) continue;
    if (out.some((o) => len2(o.blocks[0].x - cx, o.blocks[0].z - cz) < 24)) continue;
    const ux = alongX ? 1 : 0, uz = alongX ? 0 : 1;
    const pa = { x: cx - ux * span, z: cz - uz * span }, pb = { x: cx + ux * span, z: cz + uz * span };
    const ga = w.terrain(pa.x, pa.z), gb = w.terrain(pb.x, pb.z);
    if (Math.abs(ga - gb) > 1.2) continue;
    const H = rng.range(3.6, 4.6); // underside of the lintel above the higher pier foot
    const top = Math.max(ga, gb) + H;
    const blocks: StoneBlock[] = [], boxes: Box[] = [];
    const variant = 200 + v++;
    for (const [p, g] of [[pa, ga], [pb, gb]] as const) {
      const n = rng.chance(0.55) ? 3 : 2;
      const foot = g - 0.25;
      // courses of uneven height, each a little narrower and off-centre, as stacked by hand
      const hs: number[] = [];
      for (let k = 0; k < n; k++) hs.push(rng.range(0.75, 1.3) * (1 - k * 0.12));
      const sum = hs.reduce((a, b) => a + b, 0);
      let y = foot;
      for (let k = 0; k < n; k++) {
        const hgt = ((top - foot) * hs[k]) / sum;
        const sh = (1 - k * 0.09) * rng.range(0.85, 1.08);
        blocks.push({
          x: p.x + rng.range(-0.12, 0.12), y: y + hgt / 2, z: p.z + rng.range(-0.12, 0.12),
          hx: (alongX ? pier : depth) * sh, hy: hgt / 2 - 0.015, hz: (alongX ? depth : pier) * sh, yaw: rng.range(-0.06, 0.06),
        });
        y += hgt;
      }
      boxes.push({ kind: 'stone', x: p.x, z: p.z, hx: alongX ? pier : depth, hz: alongX ? depth : pier, y0: foot, y1: top, variant });
    }
    // the lintel overhangs both piers; capstones sit on it
    const lt = rng.range(0.75, 0.95), lhx = span + pier + rng.range(0.2, 0.5), lhz = depth * 1.05;
    blocks.push({ x: cx, y: top + lt / 2, z: cz, hx: alongX ? lhx : lhz, hy: lt / 2, hz: alongX ? lhz : lhx, yaw: rng.range(-0.03, 0.03) });
    boxes.push({ kind: 'stone', x: cx, z: cz, hx: alongX ? lhx : lhz, hz: alongX ? lhz : lhx, y0: top, y1: top + lt, variant });
    const caps = rng.chance(0.5) ? 2 : 1;
    for (let k = 0; k < caps; k++) {
      const off = caps === 1 ? rng.range(-0.4, 0.4) : (k ? 1 : -1) * span * 0.55;
      const ch = rng.range(0.45, 0.7), cw = rng.range(0.5, 0.75);
      blocks.push({ x: cx + ux * off, y: top + lt + ch / 2, z: cz + uz * off, hx: alongX ? cw : depth * 0.7, hy: ch / 2, hz: alongX ? depth * 0.7 : cw, yaw: rng.range(-0.25, 0.25) });
      boxes.push({ kind: 'stone', x: cx + ux * off, z: cz + uz * off, hx: alongX ? cw : depth * 0.7, hz: alongX ? depth * 0.7 : cw, y0: top + lt, y1: top + lt + ch, variant });
    }
    for (const b of boxes) w.boxes.push(b);
    out.push({ variant, blocks, boxes });
  }
}

/** Seeded push for a toppled stone's blocks (both ends compute the same numbers from the event). */
export function toppleImpulses(s: Stone, idx: number, bx: number, by: number, bz: number, force: number) {
  const out: { vx: number; vy: number; vz: number; wx: number; wy: number; wz: number; lever: number }[] = [];
  const rng = new Rng(hash32(idx, Math.round(bx * 10), Math.round(bz * 10), 0x70b1));
  const k = clamp(force / s.force, 1, 3);
  if (s.kind === StoneKind.Landform) return landformImpulses(s, rng, bx, bz, k);
  for (const b of s.blocks) {
    let dx = b.x - bx, dy = b.y - by, dz = b.z - bz;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
    dx /= d; dy /= d; dz /= d;
    // nearer blocks take more; a big shove away from the blast plus a tip about the foot, so a
    // stack comes apart and goes over rather than hopping in place under its own lintel
    const near = clamp(1.4 - d / 6, 0.3, 1.2);
    // standing stones and landforms tip over about their foot; an archway's stack is knocked apart
    const mono = s.kind !== StoneKind.Archway;
    const sp = (mono ? 2.4 : 7) * k * near;
    const hl = Math.hypot(dx, dz) || 1, hx = dx / hl, hz = dz / hl;
    const tip = (mono ? 1.8 : 2.2) * k * near;
    out.push({
      vx: hx * sp * 0.9 + rng.range(-0.8, 0.8) * near, vy: Math.max(0, dy) * sp * 0.25 + 1.4 * near, vz: hz * sp * 0.9 + rng.range(-0.8, 0.8) * near,
      wx: hz * tip + rng.range(-1.2, 1.2) * near, wy: rng.range(-0.8, 0.8) * near, wz: -hx * tip + rng.range(-1.2, 1.2) * near,
      // a monolith is pushed high up its shaft so it pivots about its foot
      lever: mono ? 0.55 : 0.2,
    });
  }
  return out;
}

/**
 * A landform goes over as a column pivoting on its broken stub, away from the blast: each drum
 * starts with the velocity of that rigid swing (so the stack leans and comes apart as it falls,
 * rather than sitting tight on its stub); an arch's deck slabs just lose their legs and drop.
 */
function landformImpulses(s: Stone, rng: Rng, bx: number, bz: number, k: number) {
  const cx = (s.x0 + s.x1) / 2, cz = (s.z0 + s.z1) / 2;
  let hx = cx - bx, hz = cz - bz;
  const hl = Math.hypot(hx, hz) || 1;
  hx /= hl; hz /= hl;
  const w0 = 0.45 * Math.sqrt(k);
  const wx = hz * w0, wz = -hx * w0;
  return s.blocks.map((b) => {
    const drum = Math.abs(b.hx - b.hz) < 1e-6;
    if (!drum) {
      // a deck slab: knocked off its legs, so it can't hold them up
      return { vx: hx * 3 + rng.range(-0.8, 0.8), vy: 0.6, vz: hz * 3 + rng.range(-0.8, 0.8), wx: rng.range(-0.4, 0.4) + wx * 0.8, wy: rng.range(-0.3, 0.3), wz: rng.range(-0.4, 0.4) + wz * 0.8, lever: 0 };
    }
    // pivot: the top of the nearest stub
    let pv = s.stumps[0];
    for (const t of s.stumps) if (len2(t.x - b.x, t.z - b.z) < len2(pv.x - b.x, pv.z - b.z)) pv = t;
    const rx = b.x - pv.x, ry = b.y - (pv.y + pv.hy), rz = b.z - pv.z;
    // an arch's legs are short and loaded: they need a harder swing than a free spire
    // each joint lets go a little differently: the drums splay and roll apart as they come down
    // instead of landing end to end like a felled log
    const j = rng.range(0.75, 1.25) * (s.stumps.length > 1 ? 1.8 : 1);
    const side = rng.range(-1.6, 1.6);
    return {
      vx: -wz * ry * j - hz * side, vy: (wz * rx - wx * rz) * j, vz: wx * ry * j + hx * side,
      wx: wx * j + rng.range(-0.35, 0.35), wy: rng.range(-0.9, 0.9), wz: wz * j + rng.range(-0.35, 0.35), lever: 0,
    };
  });
}
